import { Component, OnInit, signal, inject, ViewChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SiteSettingsService, SiteSetting } from '../../../services/site-settings.service';
import { TeamMemberService, TeamMember } from '../../../services/team-member.service';
import { ModalService } from '../../../services/modal.service';

interface SettingGroup {
  section: string;
  settings: SiteSetting[];
}

interface PageTab {
  id: string;
  label: string;
  sections: string[];
}

@Component({
  selector: 'app-site-settings',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './site-settings.html',
  styleUrl: './site-settings.css'
})
export class SiteSettings implements OnInit {
  private settingsService = inject(SiteSettingsService);
  private teamMemberService = inject(TeamMemberService);
  private modalService = inject(ModalService);

  @ViewChild('teamMemberModalTemplate') teamMemberModalTemplate!: TemplateRef<any>;

  settingGroups = signal<SettingGroup[]>([]);
  isLoading = signal<boolean>(true);
  isSaving = signal<boolean>(false);
  isPublishing = signal<boolean>(false);
  saveMessage = signal<string>('');
  errorMessage = signal<string>('');
  activeTab = signal<string>('homepage');

  // Track files for upload
  fileUploads = new Map<number, File>();

  // Team Members
  teamMembers = signal<TeamMember[]>([]);
  editingMember = signal<TeamMember | null>(null);
  memberForm = {
    name: '',
    title: '',
    description: '',
    is_active: true,
  };
  memberImageFile: File | null = null;
  memberImagePreview = signal<string | null>(null);
  isSavingMember = signal(false);

  // Page tabs configuration
  pageTabs: PageTab[] = [
    {
      id: 'homepage',
      label: 'Homepage',
      sections: ['home_hero', 'home_services', 'home_cta', 'home_blog', 'home_contact']
    },
    {
      id: 'services',
      label: 'Services',
      sections: ['services_hero', 'services_categories', 'services_why_choose']
    },
    {
      id: 'about',
      label: 'About Us',
      sections: ['about_hero', 'about_purpose', 'about_values', 'about_founder', 'about_team', 'about_cta']
    },
    {
      id: 'seo',
      label: 'SEO & Meta Tags',
      sections: ['seo_meta']
    },
    {
      id: 'general',
      label: 'General',
      sections: ['contact', 'footer', 'social']
    },
    {
      id: 'email',
      label: 'Email Settings',
      sections: ['email_otp', 'email_booking']
    },
    {
      id: 'others',
      label: 'Others',
      sections: ['others']
    }
  ];

  ngOnInit() {
    this.loadSettings();
    this.loadTeamMembers();
  }

  loadTeamMembers() {
    this.teamMemberService.getAll().subscribe({
      next: (res) => {
        if (res.status) this.teamMembers.set(res.data);
      },
    });
  }

  openAddMember() {
    this.editingMember.set(null);
    this.memberForm = { name: '', title: '', description: '', is_active: true };
    this.memberImageFile = null;
    this.memberImagePreview.set(null);
    this.modalService.open(this.teamMemberModalTemplate, '520px');
  }

  openEditMember(member: TeamMember) {
    this.editingMember.set(member);
    this.memberForm = {
      name: member.name,
      title: member.title,
      description: member.description || '',
      is_active: member.is_active,
    };
    this.memberImageFile = null;
    this.memberImagePreview.set(member.image_url);
    this.modalService.open(this.teamMemberModalTemplate, '520px');
  }

  closeTeamMemberModal() {
    this.modalService.close();
    this.editingMember.set(null);
    this.memberImageFile = null;
    this.memberImagePreview.set(null);
  }

  onMemberImageSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.memberImageFile = input.files[0];
      const reader = new FileReader();
      reader.onload = (e) => this.memberImagePreview.set(e.target?.result as string);
      reader.readAsDataURL(input.files[0]);
    }
  }

  saveMember() {
    if (!this.memberForm.name || !this.memberForm.title) return;

    this.isSavingMember.set(true);
    const fd = new FormData();
    fd.append('name', this.memberForm.name);
    fd.append('title', this.memberForm.title);
    fd.append('description', this.memberForm.description);
    fd.append('is_active', this.memberForm.is_active ? '1' : '0');
    if (this.memberImageFile) {
      fd.append('image', this.memberImageFile);
    }

    const editing = this.editingMember();
    const request = editing
      ? this.teamMemberService.update(editing.id, fd)
      : this.teamMemberService.create(fd);

    request.subscribe({
      next: () => {
        this.loadTeamMembers();
        this.closeTeamMemberModal();
        this.isSavingMember.set(false);
      },
      error: () => this.isSavingMember.set(false),
    });
  }

  deleteMember(member: TeamMember) {
    if (!confirm(`Delete team member "${member.name}"?`)) return;
    this.teamMemberService.delete(member.id).subscribe({
      next: () => this.loadTeamMembers(),
    });
  }

  toggleMemberActive(member: TeamMember) {
    const fd = new FormData();
    fd.append('is_active', member.is_active ? '0' : '1');
    this.teamMemberService.update(member.id, fd).subscribe({
      next: () => this.loadTeamMembers(),
    });
  }

  loadSettings() {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.settingsService.getAdminSettings().subscribe({
      next: (response) => {
        if (response.status) {
          const grouped: SettingGroup[] = [];

          for (const [section, settings] of Object.entries(response.data)) {
            grouped.push({
              section,
              settings: settings as SiteSetting[]
            });
          }

          this.settingGroups.set(grouped);
        }
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading settings:', error);
        this.errorMessage.set('Failed to load settings');
        this.isLoading.set(false);
      }
    });
  }

  selectTab(tabId: string) {
    this.activeTab.set(tabId);
  }

  getActiveTabLabel(): string {
    const tab = this.pageTabs.find(t => t.id === this.activeTab());
    return tab?.label || 'Settings';
  }

  getFilteredGroups(): SettingGroup[] {
    const activeTabConfig = this.pageTabs.find(t => t.id === this.activeTab());
    if (!activeTabConfig) return [];

    return this.settingGroups().filter(group =>
      activeTabConfig.sections.includes(group.section)
    );
  }

  getSectionTitle(section: string): string {
    if (section === 'seo_meta') return 'SEO Meta Tags';
    if (section === 'others') return 'Others';
    if (section === 'email_otp') return 'OTP Verification Email';
    if (section === 'email_booking') return 'Booking Confirmation Email';

    const cleanSection = section
      .replace(/^(home_|services_|about_)/, '')
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return cleanSection;
  }

  // Settings with keys containing '_hide_' or '_toggle_' are rendered as toggles
  isToggleSetting(setting: SiteSetting): boolean {
    return setting.key.includes('_hide_') || setting.key.includes('_toggle_');
  }

  onFileSelected(event: Event, setting: SiteSetting) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.fileUploads.set(setting.id!, input.files[0]);
    }
  }

  getFileName(setting: SiteSetting): string {
    if (this.fileUploads.has(setting.id!)) {
      return this.fileUploads.get(setting.id!)!.name;
    }
    return setting.value ? setting.value.split('/').pop() || '' : 'No file selected';
  }

  hasFileSelected(setting: SiteSetting): boolean {
    return this.fileUploads.has(setting.id!);
  }

  getMediaPreview(setting: SiteSetting): string {
    if (setting.full_url) {
      return setting.full_url;
    }
    if (setting.value && setting.value.startsWith('http')) {
      return setting.value;
    }
    return setting.value || '';
  }

  async saveAndPublish() {
    this.isSaving.set(true);
    this.saveMessage.set('');
    this.errorMessage.set('');

    try {
      // Get all settings that need to be saved
      const allSettings = this.settingGroups().flatMap(g => g.settings);

      // Save settings with files first
      for (const setting of allSettings) {
        if (this.fileUploads.has(setting.id!)) {
          const file = this.fileUploads.get(setting.id!)!;
          await this.settingsService.updateSettingWithFile(setting.id!, file).toPromise();
          this.fileUploads.delete(setting.id!);
        }
      }

      // Bulk save all text settings
      const textSettings: { [key: string]: string } = {};
      for (const setting of allSettings) {
        if (!['image', 'video'].includes(setting.type)) {
          textSettings[setting.key] = setting.value;
        }
      }

      if (Object.keys(textSettings).length > 0) {
        await this.settingsService.bulkUpdate(textSettings).toPromise();
      }

      this.isSaving.set(false);
      this.isPublishing.set(true);

      // Now publish to JSON
      this.settingsService.publishSettings().subscribe({
        next: (response) => {
          if (response.status) {
            this.saveMessage.set('All changes saved and published to live site!');
            setTimeout(() => this.saveMessage.set(''), 5000);
            this.loadSettings(); // Reload to get updated URLs
          } else {
            this.errorMessage.set(response.message || 'Failed to publish settings');
          }
          this.isPublishing.set(false);
        },
        error: (error) => {
          console.error('Error publishing settings:', error);
          this.errorMessage.set('Failed to publish settings');
          this.isPublishing.set(false);
        }
      });

    } catch (error) {
      console.error('Error saving settings:', error);
      this.errorMessage.set('Failed to save settings');
      this.isSaving.set(false);
      this.isPublishing.set(false);
    }
  }

  publishSettings() {
    this.isPublishing.set(true);
    this.saveMessage.set('');
    this.errorMessage.set('');

    this.settingsService.publishSettings().subscribe({
      next: (response) => {
        if (response.status) {
          this.saveMessage.set('Settings published to live site!');
          setTimeout(() => this.saveMessage.set(''), 5000);
        } else {
          this.errorMessage.set(response.message || 'Failed to publish settings');
        }
        this.isPublishing.set(false);
      },
      error: (error) => {
        console.error('Error publishing settings:', error);
        this.errorMessage.set('Failed to publish settings');
        this.isPublishing.set(false);
      }
    });
  }
}